import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatMenu } from '@angular/material/menu';
import { BaseViewComponent, ChipDateRangeSelectData } from '@sb/library/base';
import { SBDate } from '@sb/library/localization';
import { ChipButtonComponent } from '../public-api';
import { ChipDateRangeSelectBottomSheetComponent } from './bottom-sheet/chip-date-range-select-bottom-sheet.component';
import { ChipDateRangeSelectPanelComponent } from './panel/chip-date-range-select-panel.component';

@Component({
    selector: 'lib-chip-date-range-select',
    templateUrl: './chip-date-range-select.component.html',
    styleUrls: ['./chip-date-range-select.component.scss']
})
export class ChipDateRangeSelectComponent extends BaseViewComponent {

    @Input() dateOptions!: ChipDateRangeSelectData[];
    @Input() resetDateRange!: SBDate[] | null;
    @Input() loading: boolean = false;
    @Input() qaTag: string  = '';

    @ViewChild("chipButton") chipButton!: ChipButtonComponent;
    @ViewChild(ChipDateRangeSelectPanelComponent, {static: true}) panel!: ChipDateRangeSelectPanelComponent;

    private _currentSelection: ChipDateRangeSelectData | null = null;

    private _startDate: SBDate | null = null;

    private _endDate: SBDate | null = null;

    private _updated: boolean = false;

    @Input()
    set endDate(value: string) {
        if (value) {
            this._endDate = SBDate.FromISO(value);
        } else {
            this._endDate = null;
        }
    }

    @Input()
    set startDate(value: string) {
        if (value) {
            this._startDate = SBDate.FromISO(value);
        } else {
            this._startDate = null;
        }
    }

    @Output() selectionChange: EventEmitter<SBDate[] | null> = new EventEmitter();

    private _menuTrigger!: MatMenu;

    constructor(private _MatBottomSheet: MatBottomSheet) {
        super();
    }

    PanelClosed(): void {
        if (!this._updated) {
            return;
        }
        if (this._startDate && this._endDate) {
            this.selectionChange.emit([this._startDate, this._endDate]);
        } else if (this.resetDateRange) {
            this.UpdateStartEndDate(this.resetDateRange);
            this.selectionChange.emit(this.resetDateRange);
        } else {
            this.selectionChange.emit(null);
        }
        this._updated = false;
    }

    UpdateStartEndDate(event: SBDate[] | null) {
        if (event) {
            this._startDate = event[0];
            this._endDate = event[1];
        } else {
            this._startDate = null;
            this._endDate = null;
        }
        this._updated = true;
    }

    CloseMenu(): void {
        if (this._startDate && this._endDate) {
            this.chipButton.matMenuTrigger.closeMenu();
        }
    }

    MenuSelectionChanged(event: ChipDateRangeSelectData | null) {
        this._currentSelection = event;
        this._updated = true;
        if (event) {
            this.CloseMenu();
        }
    }

    DateSelectClicked(): void {
        if (this.isMobile) {
            const bottomSheetRef = this._MatBottomSheet.open(ChipDateRangeSelectBottomSheetComponent, {
                data: {DateOptions: this.dateOptions, CurrentDateRange: [this._startDate, this._endDate], CurrentSelection: this._currentSelection},
                disableClose: true
            });

            bottomSheetRef.afterDismissed().subscribe((data: {Option: ChipDateRangeSelectData, DateRange: SBDate[] | null}) => {
                if (data && data.DateRange![0] != this._startDate || data.DateRange![1] != this._endDate) {
                    if (!data.DateRange![1]) {
                        this.UpdateStartEndDate([data.DateRange![0], data.DateRange![0]]);
                    } else {
                        this.UpdateStartEndDate(data.DateRange);
                    }
                    this.MenuSelectionChanged(data.Option);
                }
                this.PanelClosed();
            });
        }
    }

    get menuTrigger(): MatMenu {
        return this._menuTrigger;
    }

    get singleDaySelected(): boolean {
        return this.startDateISO == this.endDateISO;
    }

    get currentSelection(): ChipDateRangeSelectData | null {
        return this._currentSelection;
    }

    get startDateISO(): string | null {
        return this._startDate ? this._startDate.ToISO() : null;
    }

    get endDateISO(): string | null {
        return this._endDate ? this._endDate.ToISO() : null;
    }

    get startSBDate(): SBDate | null {
        return this._startDate;
    }

    get endSBDate(): SBDate | null {
        return this._endDate;
    }
}
